import { createReducer } from 'typesafe-actions';
import { defaultErrorHandler, defaultRequestHandler } from '../dataFetching/generators';
import { map } from '../../../common/utils/fp';
import { extractImageFromUrl } from '../utils';
import { CategoriesState, CategoryDataResponseFromApi, CategoryData, CategoryTeaserData } from './types';
import { CategoriesActionType, categoryActions } from './actions';
import { normalizeCategories } from './categoriesNormalizer';
import { filterHiddenCategories } from './filterHiddenCategories';

const defaultCategoriesState: CategoriesState = {
    identifier: '',
    isFetching: false,
    normalized: null,
    originalCategories: [],
    visibleOriginalCategories: [],
    didInvalidate: false,
};

export const categoriesCollector = (depthLevel = 0): ((list: CategoryDataResponseFromApi[]) => CategoryData[]) =>
    map(
        (category: CategoryDataResponseFromApi): CategoryData => {
            const mapCategoryChildrenToBellaNaming = categoriesCollector(depthLevel + 1);
            const categoriesChildren = category.children || ([] as CategoryDataResponseFromApi[]);
            const children = mapCategoryChildrenToBellaNaming(categoriesChildren);
            let flyoutData: CategoryTeaserData | undefined;
            if (category.flyoutData) {
                const image = category.flyoutData.header_filyout_image || '';
                const imageAlt = category.flyoutData.header_filyout_image_alt;
                flyoutData = {
                    title: category.flyoutData.header_filyout_title,
                    image,
                    ctaText: category.flyoutData.header_filyout_cta_text,
                    ctaLink: category.flyoutData.header_filyout_cta_link,
                    trackingId: category.flyoutData.header_filyout_tracking,
                    // maybe we move this into the api later
                    imageAlt: imageAlt || extractImageFromUrl(image),
                };
            }

            return {
                depthLevel,
                name: category.name,
                url: category.url_key,
                children,
                flyoutData,
                shortName: category.category_shortname,
                allProductsText: category.category_all_products_text,
                hexColor: category.category_color,
                isHidden: category.category_nav_hidden,
            };
        }
    );

const mapApiCategoryToBellaNaming = categoriesCollector();
const categoriesReducer = createReducer<CategoriesState, CategoriesActionType>(defaultCategoriesState)
    .handleAction(categoryActions.actions.success, (state, action) => {
        const categoryApiData = action.payload.data.data || [];
        const originalCategories = mapApiCategoryToBellaNaming(categoryApiData);
        const visibleOriginalCategories = filterHiddenCategories(originalCategories);
        const normalized = normalizeCategories(categoryApiData);

        return {
            ...state,
            originalCategories,
            isFetching: false,
            normalized,
            visibleOriginalCategories,
        };
    })
    .handleAction(categoryActions.actions.failure, defaultErrorHandler)
    .handleAction(categoryActions.actions.request, defaultRequestHandler);

export default categoriesReducer;
